'use strict';
import * as PropTypes from 'prop-types';
import React, { PureComponent } from "react";
import _ from 'lodash';
import { createRef } from 'react';

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error) {
		// 更新 state 使下一個 render 顯示降級 UI
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		// 你也可以將錯誤日誌上傳至伺服器
		console.error("Uncaught error:", this.props.row, error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			// 你可以自訂降級後的 UI 並顯示
			return <h1>Something went wrong.</h1>;
		}

		return this.props.children;
	}
}

// 只用在 ImportDialog 的預覽表格中，有機會可以把名稱換了
class VirtualList extends PureComponent {
	static propTypes = {
		rows: PropTypes.array,
		cols: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		itemHeight: PropTypes.number,
		children: PropTypes.func,
		scrollRef: PropTypes.oneOfType([
			PropTypes.func,
			PropTypes.shape({ current: PropTypes.any })
		]),
		showItems: PropTypes.number,
		title: PropTypes.string,
		className: PropTypes.string,
		header: PropTypes.object,
		mergeRows: PropTypes.number,
		tableClassName: PropTypes.string,
	};

	static defaultProps = {
		itemHeight: 40,
		rows: [],
		showItems: 20,
		cols: {},
		title: "",
		mergeRows: 1,
		tableClassName: 'table-validateResult'
	};

	constructor(props) {
		super(props);
		this.state = {
			currentRows: [],
			topItemCount: 0,
			botItemCount: 0,
		};
		this.onScroll = this._onScroll.bind(this);
		this.table = createRef();
		this.isAddEventListener = false;
	}

	componentDidMount() {
		this.setRows();
	}

	componentDidUpdate(prevProps) {
		const { scrollRef } = this.props;
		if (scrollRef.current) {
			const container = scrollRef?.current?._dom?.root;
			if (container && !this.isAddEventListener) {
				container.addEventListener?.('scroll', this.onScroll);
				this.isAddEventListener = true;
			}
		}
		if (prevProps.rows !== this.props.rows) {
			const container = scrollRef?.current?._dom?.root;
			// container.scrollTop = 0;
			this._onScroll();
		}
	}

	componentWillUnmount() {
		const { scrollRef } = this.props;
		scrollRef?.current?._dom?.root?.removeEventListener?.('scroll', this.onScroll);
	}

	setRows(cb) {
		const { rows, showItems } = this.props;
		this.setState({ currentRows: rows.slice(0, showItems), botItemCount: rows.length - showItems  }, cb);
	}

	_onScroll() {
		const { scrollRef, itemHeight, rows, showItems, mergeRows } = this.props;
		const container = scrollRef.current?._dom?.root;
		// 滾輪相對於頂部的距離
		const { scrollTop } = container;
		// 計算頂部的空間距，當超過整個捲動空間時直接相減
		const topItemCount = Math.floor(scrollTop / itemHeight) + showItems > rows.length ? rows.length - showItems : Math.floor(scrollTop / itemHeight);
		const botItemCount =  topItemCount + showItems > rows.length ? 0: rows.length - topItemCount - showItems;

		// 計算 mergeRows 進行間隔渲染
		// const currentRows = rows.length > showItems ? rows.slice(topItemCount, topItemCount + showItems) : rows;
		// if (topItemCount % mergeRows === 0) {
		// 	this.setState({ topItemCount: topItemCount, botItemCount, currentRows });
		// }

		if (mergeRows === 1) {
			const currentRows = rows.length > showItems ? rows.slice(topItemCount, topItemCount + showItems) : rows;
			this.setState({ topItemCount: topItemCount < 0 ? 0 : topItemCount, botItemCount, currentRows });
		} else {
			const offset = topItemCount % mergeRows;
			const currentRows = rows.length > showItems ? rows.slice(topItemCount - offset, topItemCount + showItems + offset) : rows;
			this.setState({ topItemCount: topItemCount - offset, botItemCount, currentRows });
		}

	}

	render() {
		const { itemHeight, children, cols, title, className, rows, showItems, header, tableClassName } = this.props;
		const { currentRows, botItemCount, topItemCount } = this.state;
		const otherProps = {
			style: { height: `${itemHeight}px` }
		};

		return (
			<table ref={this.table} className={`table table-bordered ${tableClassName} ${className}`}>
				<thead>
					{header ? header : <tr>
						<th>#</th>
						{title ?  <th>{ title }</th>: null}
						{_.values(cols || {}).map((col, idx) => <th key={`col_${idx}`}>{col}</th>)}
					</tr>}
				</thead>
				<tbody>
					<tr style={{ height: `${topItemCount * itemHeight}px` }} />
					{
						currentRows?.map((row, idx) => {
							return <React.Fragment key={idx}>
								<ErrorBoundary row={row}>
									{
										rows.length > showItems ?
										children(row, topItemCount + idx, otherProps ) :
										children(row, idx)
									}
								</ErrorBoundary>
							</React.Fragment>;
						})
					}
					<tr style={{ height: `${botItemCount * itemHeight}px` }} />
				</tbody>
			</table>
		);
	}
}

class VirtualListBasic extends PureComponent {
	static propTypes = {
		rows: PropTypes.array,
		itemHeight: PropTypes.number,
		children: PropTypes.func,
		container: PropTypes.oneOfType([
			PropTypes.func,
			PropTypes.node,
			PropTypes.shape({ current: PropTypes.any })
		]),
		showItems: PropTypes.number,
		title: PropTypes.string,
		className: PropTypes.string,
	};

	static defaultProps = {
		itemHeight: 40,
		rows: [],
		showItems: 20,
		title: "",
		mergeRows: 1,
	};

	constructor(props) {
		super(props);
		this.state = {
			currentRows: [],
			topItemCount: 0,
			botItemCount: 0,
		};
		this.onScroll = this._onScroll.bind(this);
		this.isAddEventListener = false;
	}

	componentDidMount() {
		this.setRows();
	}

	componentDidUpdate(prevProps) {
		const container = this.getContainer();
		if (container) {
			if (container && !this.isAddEventListener) {
				container.addEventListener?.('scroll', this.onScroll);
				this.isAddEventListener = true;
			}
		} else {
			console.warn('VirtualListBasic need container props');
		}
		if (prevProps.rows !== this.props.rows) {
			// container.scrollTop = 0;
			this._onScroll();
		}
	}

	componentWillUnmount() {
		const container = this.getContainer();
		container?.removeEventListener?.('scroll', this.onScroll);
	}

	getContainer() {
		const { container } = this.props;
		return container?.current || container;
	}

	setRows(cb) {
		const { rows, showItems } = this.props;
		this.setState({ currentRows: rows.slice(0, showItems), botItemCount: rows.length - showItems  }, cb);
	}

	getTopItemCount() {
		const container = this.getContainer();
		// 滾輪相對於頂部的距離
		const { scrollTop } = container;
		const topItemCount = Math.floor(scrollTop / itemHeight) + showItems > rows.length ? rows.length - showItems : Math.floor(scrollTop / itemHeight);

		return {
			topItemCount,
		};
	}

	_onScroll() {
		const { itemHeight, rows, showItems } = this.props;
		const container = this.getContainer();
		// 滾輪相對於頂部的距離
		const { scrollTop } = container;
		// 計算頂部的空間距，當超過整個捲動空間時直接相減
		const topItemCount = Math.floor(scrollTop / itemHeight) + showItems > rows.length ? rows.length - showItems : Math.floor(scrollTop / itemHeight);
		const botItemCount =  topItemCount + showItems > rows.length ? 0: rows.length - topItemCount - showItems;

		const currentRows = rows.length > showItems ? rows.slice(topItemCount, topItemCount + showItems) : rows;
		this.setState({ topItemCount: topItemCount < 0 ? 0 : topItemCount, botItemCount, currentRows });
	}

	render() {
		const { itemHeight, children, rows, showItems } = this.props;
		const { currentRows, botItemCount, topItemCount } = this.state;
		const otherProps = {
			style: { height: `${itemHeight}px` }
		};

		return (
			<>
				<div style={{ height: `${topItemCount * itemHeight}px` }} />
				{
					currentRows?.map((row, idx) => {
						return <React.Fragment key={idx}>
							{
								rows.length > showItems ?
								children(row, topItemCount + idx, otherProps ) :
								children(row, idx)
							}
						</React.Fragment>;
					})
				}
				<div style={{ height: `${botItemCount * itemHeight}px` }} />
			</>
		);
	}
}

export {
	VirtualList,
	VirtualListBasic
};
